// IngredientsHub.js
// Full-page ingredient management hub.
// Left panel: searchable ingredient list with nutrition status indicators.
// Right panel: Info tab (fields + price history chart), Nutrition tab, Documents tab.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ingredientService from '../services/ingredientService';
import documentService from '../services/documentService';
import documentCategoryService from '../services/documentCategoryService';
import supplierService from '../services/supplierService';
import { aiExtractNutrition } from '../services/nutritionService';
import { IngredientNutritionRow } from './NutritionFactsPanel';
import ConfirmModal from './ConfirmModal';
import SupplierPickerField from './SupplierPickerField';
import SearchableSelect from './SearchableSelect';
import qboService from '../services/qboService';
import { usePermissions } from '../context/PermissionContext';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const ALLERGENS = [
  { key: 'milk',      label: 'Milk' },
  { key: 'eggs',      label: 'Eggs' },
  { key: 'fish',      label: 'Fish' },
  { key: 'shellfish', label: 'Shellfish' },
  { key: 'tree_nuts', label: 'Tree Nuts' },
  { key: 'wheat',     label: 'Wheat' },
  { key: 'peanuts',   label: 'Peanuts' },
  { key: 'soybeans',  label: 'Soybeans' },
  { key: 'sesame',    label: 'Sesame' },
];

const BLANK_ADD = {
  name: '', manufacturerId: '', supplierId: '', category: '',
  rawMaterialCode: '', caseSize: '', caseSizeUnit: 'kg', caseLabel: '', labelName: '',
};

const LB_TO_KG = 0.453592;

const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];
const BLANK_UPLOAD = { name: '', documentCategoryId: '', expiryDate: '', notes: '' };

function expiryStatus(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(dateStr + 'T12:00:00');
  if (exp < today) return 'expired';
  if (exp <= new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)) return 'critical';
  if (exp <= new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000)) return 'expiring';
  return 'ok';
}

function ExpiryBadge({ date }) {
  const s = expiryStatus(date);
  if (!s) return null;
  const cfg = {
    expired:  { cls: 'bg-danger',           label: 'Expired' },
    critical: { cls: 'bg-danger',           label: `Exp. ${date}` },
    expiring: { cls: 'bg-warning text-dark', label: `Exp. ${date}` },
    ok:       { cls: 'bg-success',           label: `Exp. ${date}` },
  }[s];
  return <span className={`badge ${cfg.cls}`} style={{ fontSize: 10 }}>{cfg.label}</span>;
}

function nutritionDot(ing) {
  const n = ing.Nutrition || ing.nutrition;
  if (!n) return { color: '#dc3545', title: 'No nutrition data', label: '●' };
  if (n.dataSource === 'fdc') return { color: '#198754', title: 'FDC data', label: '●' };
  return { color: '#ffc107', title: 'Manual data', label: '●' };
}

function PriceChart({ history }) {
  if (!history || history.length < 2) return null;

  const sorted = [...history].sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate));
  const labels = sorted.map(p => {
    const d = new Date(p.effectiveDate + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  });
  const values = sorted.map(p => Number(p.pricePerKg));

  const data = {
    labels,
    datasets: [{
      label: '$/kg',
      data: values,
      borderColor: '#2f80ed',
      backgroundColor: 'rgba(47,128,237,0.10)',
      borderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      fill: true,
      tension: 0.3,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => `$${Number(ctx.raw).toFixed(4)}/kg`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: v => `$${Number(v).toFixed(2)}`,
          font: { size: 11 },
        },
        grid: { color: 'rgba(0,0,0,0.06)' },
      },
      x: {
        ticks: { font: { size: 11 }, maxRotation: 40 },
        grid: { display: false },
      },
    },
  };

  return (
    <div style={{ height: 180 }}>
      <Line data={data} options={options} />
    </div>
  );
}

export default function IngredientsHub({ ingredients, onIngredientsChange, currentPrices, initialIngredientId = null, initialTab = null, onInitialNavConsumed = null }) {
  const [selectedId, setSelectedId] = useState(null);
  const [detailTab, setDetailTab] = useState('info');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ ...BLANK_ADD });
  const [addSaving, setAddSaving] = useState(false);

  // Info tab form
  const [editForm, setEditForm] = useState(null);
  const [infoSaving, setInfoSaving] = useState(false);

  // Sub-ingredients + allergens
  const [subIngredients, setSubIngredients] = useState([]);
  const [allergenFlags, setAllergenFlags] = useState([]);
  // Secondary USDA/FSIS cross-check — non-blocking, see AdminProductPage regulatoryAgency
  const [isMeatOrPoultry, setIsMeatOrPoultry] = useState(false);

  // Price history
  const [priceHistory, setPriceHistory] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [newPrice, setNewPrice] = useState({ pricePerKg: '', effectiveDate: '', note: '', unit: 'kg' });
  const [priceSaving, setPriceSaving] = useState(false);

  // Supplier/manufacturer directory — see docs/SUPPLIERS_MODULE.md
  const [supplierOptions, setSupplierOptions] = useState([]);
  const loadSupplierOptions = useCallback(() => {
    supplierService.getSuppliers().then(setSupplierOptions).catch(() => {});
  }, []);

  // QBO expense/COGS accounts — for the "QBO Expense Account" picker below. Empty when QBO
  // isn't connected (backend returns []), in which case the field just shows no options.
  const [qboAccounts, setQboAccounts] = useState([]);
  const { companySettings } = usePermissions();
  const isR365 = companySettings?.accountingProvider === 'r365';

  // Supplier docs
  const [docCategories, setDocCategories] = useState([]);
  const [ingDocs, setIngDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadForm, setUploadForm] = useState({ ...BLANK_UPLOAD });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState(null);
  const fileInputRef = useRef(null);

  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [toggleActiveSaving, setToggleActiveSaving] = useState(false);

  // AI nutrition extraction
  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiPrefill, setAiPrefill] = useState(null);
  const [aiConfidence, setAiConfidence] = useState(null);
  const [aiPlausibility, setAiPlausibility] = useState(null);
  const [aiDocPickerOpen, setAiDocPickerOpen] = useState(false);
  const [aiSubIngredients, setAiSubIngredients] = useState([]);
  const [aiAllergensDraft, setAiAllergensDraft] = useState([]);
  const [aiSubApplied, setAiSubApplied] = useState(false);
  const [aiAllergensApplied, setAiAllergensApplied] = useState(false);
  const [nutritionDirty, setNutritionDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);

  // Toast notification — floats fixed, never shifts layout
  const [toast, setToast] = useState(null); // { msg, type: 'success'|'danger' }
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
  }, []);
  useEffect(() => {
    if (!toast) return;
    // Danger toasts (e.g. "cannot delete — linked to...") tend to carry more to read than a
    // short success confirmation, so give them longer before auto-dismissing.
    const t = setTimeout(() => setToast(null), toast.type === 'danger' ? 7000 : 3500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    documentCategoryService.getCategories().then(setDocCategories).catch(() => {});
    loadSupplierOptions();
    qboService.getAccounts().then(setQboAccounts).catch(() => {});
  }, [loadSupplierOptions]);

  const selected = ingredients.find(i => i.id === selectedId) || null;

  const visibleIngredients = showInactive ? ingredients : ingredients.filter(i => i.isActive !== false);
  const filtered = search.trim()
    ? visibleIngredients.filter(i => {
        const q = search.toLowerCase();
        return i.name.toLowerCase().includes(q)
          || (i.Manufacturer?.name || '').toLowerCase().includes(q)
          || (i.Supplier?.name || '').toLowerCase().includes(q)
          || (i.category || '').toLowerCase().includes(q)
          || (i.rawMaterialCode || '').toLowerCase().includes(q)
          || (i.labelName || '').toLowerCase().includes(q);
      })
    : visibleIngredients;

  const loadPrices = useCallback(async (id) => {
    setLoadingPrices(true);
    try { setPriceHistory(await ingredientService.getPrices(id)); }
    catch { setPriceHistory([]); }
    finally { setLoadingPrices(false); }
  }, []);

  const loadDocs = useCallback(async (id) => {
    setLoadingDocs(true);
    try { setIngDocs(await documentService.getDocumentsForIngredient(id)); }
    catch { setIngDocs([]); }
    finally { setLoadingDocs(false); }
  }, []);

  const selectIngredient = useCallback((ing) => {
    setSelectedId(ing.id);
    setDetailTab('info');
    setEditForm({
      name: ing.name || '',
      manufacturerId: ing.manufacturerId != null ? String(ing.manufacturerId) : '',
      supplierId: ing.supplierId != null ? String(ing.supplierId) : '',
      category: ing.category || '',
      rawMaterialCode: ing.rawMaterialCode || '',
      caseSize: ing.caseSize != null ? String(ing.caseSize) : '',
      caseSizeUnit: ing.caseSizeUnit || 'kg',
      caseLabel: ing.caseLabel || '',
      labelName: ing.labelName || '',
      isActive: ing.isActive !== false,
      qbExpenseAccountId: ing.qbExpenseAccountId || '',
      r365GLAccount: ing.r365GLAccount || '',
    });
    setSubIngredients(ing.subIngredients || []);
    setAllergenFlags(ing.allergenFlags || []);
    setIsMeatOrPoultry(Boolean(ing.isMeatOrPoultry));
    setNewPrice({ pricePerKg: '', effectiveDate: '', note: '', unit: 'kg' });
    setShowUploadForm(false);
    setUploadForm({ ...BLANK_UPLOAD });
    setUploadFile(null);
    setAiPrefill(null);
    setAiConfidence(null);
    setAiDocPickerOpen(false);
    setAiSubIngredients([]);
    setAiAllergensDraft([]);
    setAiSubApplied(false);
    setAiAllergensApplied(false);
    setNutritionDirty(false);
    setPendingTab(null);
    loadPrices(ing.id);
    loadDocs(ing.id);
  }, [loadPrices, loadDocs]);

  // Auto-select ingredient and tab when parent navigates here (e.g. from product nutrition view)
  useEffect(() => {
    if (!initialIngredientId || !ingredients.length) return;
    const ing = ingredients.find(i => i.id === initialIngredientId);
    if (!ing) return;
    selectIngredient(ing);
    if (initialTab) setDetailTab(initialTab);
    onInitialNavConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIngredientId, ingredients]);

  // Re-sync editForm when `ingredients` prop refreshes (e.g. after save)
  useEffect(() => {
    if (selectedId && selected) {
      setEditForm(prev => {
        if (!prev) return prev;
        return {
          name: selected.name || '',
          manufacturerId: selected.manufacturerId != null ? String(selected.manufacturerId) : '',
          supplierId: selected.supplierId != null ? String(selected.supplierId) : '',
          category: selected.category || '',
          rawMaterialCode: selected.rawMaterialCode || '',
          caseSize: selected.caseSize != null ? String(selected.caseSize) : '',
          caseSizeUnit: selected.caseSizeUnit || 'kg',
          caseLabel: selected.caseLabel || '',
          labelName: selected.labelName || '',
          isActive: selected.isActive !== false,
          qbExpenseAccountId: selected.qbExpenseAccountId || '',
          r365GLAccount: selected.r365GLAccount || '',
        };
      });
    }
  }, [ingredients, selectedId, selected]);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim() || !addForm.manufacturerId) {
      showToast('Name and manufacturer are required.', 'danger');
      return;
    }
    setAddSaving(true);
    try {
      const created = await ingredientService.createIngredient({
        name: addForm.name.trim(),
        manufacturerId: addForm.manufacturerId,
        supplierId: addForm.supplierId || null,
        category: addForm.category.trim() || null,
        rawMaterialCode: addForm.rawMaterialCode.trim() || null,
        caseSize: addForm.caseSize !== '' ? Number(addForm.caseSize) : null,
        caseSizeUnit: addForm.caseSizeUnit,
        caseLabel: addForm.caseLabel.trim() || null,
        labelName: addForm.labelName.trim() || null,
      });
      setAddForm({ ...BLANK_ADD });
      setShowAddForm(false);
      await onIngredientsChange();
      selectIngredient(created);
      showToast('Ingredient created.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Error creating ingredient.', 'danger');
    } finally {
      setAddSaving(false);
    }
  };

  const handleInfoSave = async () => {
    if (!editForm.name.trim() || !editForm.manufacturerId) {
      showToast('Name and manufacturer are required.', 'danger');
      return;
    }
    setInfoSaving(true);
    try {
      await ingredientService.updateIngredient(selectedId, {
        name: editForm.name.trim(),
        manufacturerId: editForm.manufacturerId,
        supplierId: editForm.supplierId || null,
        category: editForm.category.trim() || null,
        rawMaterialCode: editForm.rawMaterialCode.trim() || null,
        caseSize: editForm.caseSize !== '' ? Number(editForm.caseSize) : null,
        caseSizeUnit: editForm.caseSizeUnit,
        caseLabel: editForm.caseLabel.trim() || null,
        labelName: editForm.labelName.trim() || null,
        subIngredients,
        allergenFlags,
        isMeatOrPoultry,
        isActive: editForm.isActive,
        qbExpenseAccountId: editForm.qbExpenseAccountId || null,
        qbExpenseAccountName: editForm.qbExpenseAccountId
          ? (qboAccounts.find(a => String(a.id) === String(editForm.qbExpenseAccountId))?.name || null)
          : null,
        r365GLAccount: editForm.r365GLAccount?.trim() || null,
      });
      showToast('Changes saved.');
      await onIngredientsChange();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving.', 'danger');
    } finally {
      setInfoSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteSaving(true);
    try {
      await ingredientService.deleteIngredient(selectedId);
      setSelectedId(null);
      setEditForm(null);
      setConfirmDelete(false);
      await onIngredientsChange();
      showToast('Ingredient deleted.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Error deleting ingredient.', 'danger');
      setConfirmDelete(false);
    } finally {
      setDeleteSaving(false);
    }
  };

  // One-click archive/reactivate — a faster path to the same isActive toggle the edit form's
  // "Active" checkbox already saves, for when an ingredient can't be hard-deleted (still linked
  // to receiving logs/traceability/recipes) and the user just wants it off the active list.
  const handleToggleActive = async () => {
    const nextActive = !editForm.isActive;
    setToggleActiveSaving(true);
    try {
      await ingredientService.updateIngredient(selectedId, { isActive: nextActive });
      setEditForm(f => ({ ...f, isActive: nextActive }));
      await onIngredientsChange();
      showToast(nextActive ? 'Ingredient reactivated.' : 'Ingredient archived.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Error updating ingredient.', 'danger');
    } finally {
      setToggleActiveSaving(false);
    }
  };

  const handleAddPrice = async () => {
    if (!newPrice.pricePerKg || !newPrice.effectiveDate) {
      showToast('Price and effective date are required.', 'danger');
      return;
    }
    const pricePerKg = newPrice.unit === 'lb'
      ? Number(newPrice.pricePerKg) / LB_TO_KG
      : Number(newPrice.pricePerKg);
    setPriceSaving(true);
    try {
      const entry = await ingredientService.addPrice(selectedId, {
        pricePerKg,
        effectiveDate: newPrice.effectiveDate,
        note: newPrice.note,
      });
      setPriceHistory(prev => [entry, ...prev]);
      setNewPrice(p => ({ pricePerKg: '', effectiveDate: '', note: '', unit: p.unit }));
      await onIngredientsChange();
      showToast('Price recorded.');
    } catch {
      showToast('Error recording price.', 'danger');
    } finally {
      setPriceSaving(false);
    }
  };

  // Reset upload form when ingredient changes or tab closes
  const resetUpload = useCallback(() => {
    setShowUploadForm(false);
    setUploadForm({ ...BLANK_UPLOAD });
    setUploadFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!ALLOWED_MIME.includes(f.type)) {
      showToast('Only PDF, JPEG, and PNG files are allowed.', 'danger');
      return;
    }
    setUploadFile(f);
    if (!uploadForm.name) {
      const base = f.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      setUploadForm(fm => ({ ...fm, name: base }));
    }
    if (!uploadForm.documentCategoryId) {
      const byKey = (key) => docCategories.find(c => c.systemKey === key && c.isActive)?.id;
      const lc = f.name.toLowerCase();
      let catId = null;
      if (lc.includes('coa'))        catId = byKey('coa');
      else if (lc.includes('spec') || lc.includes('tds') || lc.includes('technical'))
                                     catId = byKey('technical_sheet');
      else if (lc.includes('guarantee')) catId = byKey('letter_of_guarantee');
      else if (lc.includes('sqf'))   catId = byKey('sqf_factory_record');
      if (catId) setUploadForm(fm => ({ ...fm, documentCategoryId: String(catId) }));
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile)           { showToast('Please select a file.', 'danger'); return; }
    if (!uploadForm.documentCategoryId) { showToast('Tab is required.', 'danger'); return; }
    if (!uploadForm.name.trim()) { showToast('Document name is required.', 'danger'); return; }
    setUploading(true);
    setUploadProgress(0);
    try {
      const { uploadUrl, publicUrl } = await documentService.getUploadUrl(
        uploadFile.name, uploadFile.type, uploadFile.size
      );
      await axios.put(uploadUrl, uploadFile, {
        headers: { 'Content-Type': uploadFile.type },
        onUploadProgress: evt => setUploadProgress(Math.round((evt.loaded / evt.total) * 100)),
      });
      await documentService.createDocument({
        name:        uploadForm.name.trim(),
        documentCategoryId: uploadForm.documentCategoryId,
        fileUrl:     publicUrl,
        fileName:    uploadFile.name,
        fileSize:    uploadFile.size,
        mimeType:    uploadFile.type,
        ingredientId: selectedId,
        expiryDate:  uploadForm.expiryDate || null,
        notes:       uploadForm.notes.trim() || null,
      });
      showToast('Document uploaded.');
      resetUpload();
      setIngDocs(await documentService.getDocumentsForIngredient(selectedId));
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed.', 'danger');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async () => {
    if (!confirmDeleteDoc) return;
    try {
      await documentService.deleteDocument(confirmDeleteDoc.id);
      setIngDocs(prev => prev.filter(d => d.id !== confirmDeleteDoc.id));
      showToast('Document deleted.');
    } catch {
      showToast('Error deleting document.', 'danger');
    } finally {
      setConfirmDeleteDoc(null);
    }
  };

  const handleAiExtract = async (docId) => {
    if (!selectedId) return;
    // Prefer docs filed under the seeded "Technical Data Sheet" category, but categories are
    // fully company-customizable (renamed/replaced via Manage Tabs), so a company that
    // reorganized theirs (e.g. into a custom "Ingredients TDS" tab with no systemKey) would
    // otherwise never see this feature at all — fall back to offering every linked document.
    const techSheetDocs = ingDocs.filter(d => d.category?.systemKey === 'technical_sheet');
    const specSheets = techSheetDocs.length > 0 ? techSheetDocs : ingDocs;
    const idToUse = docId || (specSheets.length === 1 ? specSheets[0].id : null);
    if (!idToUse) {
      // Multiple spec sheets — show picker for user to choose
      setAiDocPickerOpen(true);
      return;
    }
    setAiDocPickerOpen(false);
    setAiExtracting(true);
    setAiPrefill(null);
    setAiConfidence(null);
    setAiPlausibility(null);
    try {
      const result = await aiExtractNutrition(selectedId, idToUse);
      setAiPrefill(result.extracted);
      setAiConfidence(result.confidence);
      setAiPlausibility(result.plausibility || null);
      setAiSubIngredients(result.subIngredients || []);
      setAiAllergensDraft(result.allergens || []);
      setAiSubApplied(false);
      setAiAllergensApplied(false);
      if (result.confidence === 'low') {
        showToast('Extraction complete — low confidence. Review all values carefully.', 'danger');
      } else {
        showToast(`Extracted from "${result.docName}". Review nutrition, sub-ingredients and allergens below.`);
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'AI extraction failed.', 'danger');
    } finally {
      setAiExtracting(false);
    }
  };

  const toggleAllergen = (key) =>
    setAllergenFlags(prev => prev.includes(key) ? prev.filter(a => a !== key) : [...prev, key]);

  const addSubIngredient = () =>
    setSubIngredients(prev => [...prev, { name: '' }]);

  const updateSubIngredient = (idx, val) =>
    setSubIngredients(prev => prev.map((s, i) => i === idx ? { ...s, name: val } : s));

  const removeSubIngredient = (idx) =>
    setSubIngredients(prev => prev.filter((_, i) => i !== idx));

  const currentPriceKg = currentPrices && selectedId ? currentPrices[String(selectedId)] : null;

  // ─── Price display helpers ───────────────────────────────────────────────────
  const latestPrice = priceHistory.length > 0
    ? [...priceHistory].sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate))[0]
    : null;

  return (
    <div className="card mb-4">
      <div className="card-header text-white d-flex align-items-center justify-content-between" style={{ backgroundColor: '#1b2638' }}>
        <strong>Ingredients</strong>
        <button
          className="btn btn-sm btn-outline-light"
          onClick={() => { setShowAddForm(v => !v); setAddForm({ ...BLANK_ADD }); }}
        >
          {showAddForm ? '✕ Cancel' : '+ New Ingredient'}
        </button>
      </div>

      {/* Add ingredient inline form */}
      {showAddForm && (
        <div className="border-bottom bg-light px-3 py-3">
          <form onSubmit={handleAddSubmit}>
            <div className="row g-2 align-items-end">
              <div className="col-md-3">
                <label className="form-label form-label-sm fw-semibold">Name <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" value={addForm.name}
                  onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} placeholder="Flour Casillo" required />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm fw-semibold">Label Name</label>
                <input className="form-control form-control-sm" value={addForm.labelName}
                  onChange={e => setAddForm(f => ({ ...f, labelName: e.target.value }))} placeholder="Wheat Flour" />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm fw-semibold">Manufacturer <span className="text-danger">*</span></label>
                <SupplierPickerField
                  options={supplierOptions} value={addForm.manufacturerId}
                  onChange={v => setAddForm(f => ({ ...f, manufacturerId: v }))}
                  onCreated={loadSupplierOptions} placeholder="Casillo"
                />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm fw-semibold">Supplier</label>
                <SupplierPickerField
                  options={supplierOptions} value={addForm.supplierId}
                  onChange={v => setAddForm(f => ({ ...f, supplierId: v }))}
                  onCreated={loadSupplierOptions} placeholder="If different from manufacturer"
                />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm fw-semibold">Category</label>
                <input className="form-control form-control-sm" value={addForm.category}
                  onChange={e => setAddForm(f => ({ ...f, category: e.target.value }))} placeholder="Flour" />
              </div>
              <div className="col-md-1">
                <label className="form-label form-label-sm fw-semibold">RM Code</label>
                <input className="form-control form-control-sm" value={addForm.rawMaterialCode}
                  onChange={e => setAddForm(f => ({ ...f, rawMaterialCode: e.target.value }))} placeholder="FL-001" />
              </div>
              <div className="col-md-2 d-flex gap-1 align-items-end">
                <button type="submit" className="btn btn-primary btn-sm" disabled={addSaving}>
                  {addSaving ? 'Saving…' : 'Add'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div className="card-body p-0">
        <div className="row g-0" style={{ minHeight: 540 }}>

          {/* ── Left panel: ingredient list ── */}
          <div className="col-12 col-lg-3 border-end d-flex flex-column" style={{ maxHeight: 700 }}>
            <div className="p-2 border-bottom">
              <input
                type="text"
                className="form-control form-control-sm mb-1"
                placeholder="Search ingredients…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <div className="form-check">
                <input type="checkbox" className="form-check-input" id="hub-show-archived"
                  checked={showInactive}
                  onChange={e => setShowInactive(e.target.checked)} />
                <label className="form-check-label text-muted" htmlFor="hub-show-archived" style={{ fontSize: 11 }}>
                  Show archived
                </label>
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filtered.length === 0 && (
                <div className="text-muted p-3" style={{ fontSize: 13 }}>
                  {ingredients.length === 0 ? 'No ingredients yet. Add one above.' : 'No results.'}
                </div>
              )}
              {filtered.map(ing => {
                const dot = nutritionDot(ing);
                const price = currentPrices?.[String(ing.id)];
                const isSelected = ing.id === selectedId;
                return (
                  <button
                    key={ing.id}
                    type="button"
                    onClick={() => selectIngredient(ing)}
                    className="w-100 text-start border-0 border-bottom px-3 py-2"
                    style={{
                      background: isSelected ? '#e8f0fe' : 'transparent',
                      borderLeft: isSelected ? '3px solid #2f80ed' : '3px solid transparent',
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                    }}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <span style={{ color: dot.color, fontSize: 10, marginTop: 4, flexShrink: 0 }} title={dot.title}>●</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="fw-semibold text-truncate" style={{ fontSize: 13 }}>
                          {ing.name}
                          {ing.isActive === false && <span className="text-muted ms-1" style={{ fontSize: 11 }}>(archived)</span>}
                        </div>
                        <div className="text-muted text-truncate" style={{ fontSize: 11 }}>
                          {ing.Manufacturer?.name}
                          {ing.category ? <span className="ms-1 badge text-bg-light border" style={{ fontSize: 10, fontWeight: 400 }}>{ing.category}</span> : null}
                        </div>
                        <div className="d-flex gap-1 align-items-center mt-1 flex-wrap">
                          {ing.sourceProductId && (
                            <span className="badge text-bg-info" style={{ fontSize: 10, fontWeight: 400 }} title={`Produced internally from ${ing.SourceProduct?.name || 'a product'} via Prep CCP`}>
                              🏭 {ing.SourceProduct?.name || 'Internally produced'}
                            </span>
                          )}
                          {ing.rawMaterialCode && (
                            <span className="badge text-bg-secondary" style={{ fontSize: 10, fontWeight: 400 }}>{ing.rawMaterialCode}</span>
                          )}
                          {ing.labelName && ing.labelName !== ing.name && (
                            <span className="badge text-bg-light border text-muted" style={{ fontSize: 10, fontWeight: 400 }}>label: {ing.labelName}</span>
                          )}
                          {price != null && (
                            <span className="badge text-bg-light border" style={{ fontSize: 10, fontWeight: 500, color: '#198754' }}>
                              ${Number(price).toFixed(4)}/kg
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="border-top px-3 py-2 text-muted" style={{ fontSize: 11 }}>
              {visibleIngredients.length} ingredient{visibleIngredients.length !== 1 ? 's' : ''}
              {' · '}
              <span style={{ color: '#198754' }}>●</span> FDC{' '}
              <span style={{ color: '#ffc107' }}>●</span> Manual{' '}
              <span style={{ color: '#dc3545' }}>●</span> Missing
            </div>
          </div>

          {/* ── Right panel ── */}
          <div className="col-12 col-lg-9 d-flex flex-column">
            {!selected || !editForm ? (
              <div className="d-flex align-items-center justify-content-center text-muted h-100 p-4"
                style={{ fontSize: 14 }}>
                Select an ingredient from the list to view or edit details.
              </div>
            ) : (
              <>
                {/* Right panel header */}
                <div className="px-4 pt-3 pb-0 border-bottom">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h5 className="mb-0 fw-bold">{selected.name}</h5>
                      {selected.labelName && selected.labelName !== selected.name && (
                        <div className="text-muted" style={{ fontSize: 12 }}>Label name: {selected.labelName}</div>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className={`btn btn-sm ${editForm.isActive ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                        onClick={handleToggleActive}
                        disabled={toggleActiveSaving}
                        title={editForm.isActive ? 'Archive — hides it from active lists without deleting it' : 'Reactivate'}
                      >
                        {toggleActiveSaving ? 'Saving…' : editForm.isActive ? '📦 Archive' : '♻️ Reactivate'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setConfirmDelete(true)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <ul className="nav nav-tabs border-0">
                    {['info', 'nutrition', 'documents'].map(t => (
                      <li key={t} className="nav-item">
                        <button
                          className={`nav-link ${detailTab === t ? 'active' : ''}`}
                          style={{ fontSize: 13, textTransform: 'capitalize' }}
                          onClick={() => {
                            if (detailTab === 'nutrition' && t !== 'nutrition' && nutritionDirty) {
                              setPendingTab(t);
                            } else {
                              setDetailTab(t);
                            }
                          }}
                        >
                          {t === 'nutrition' ? 'Nutrition' : t === 'documents' ? 'Documents' : 'Info'}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Tab content */}
                <div className="p-4" style={{ overflowY: 'auto', flex: 1 }}>

                  {/* ── Info tab ── */}
                  {detailTab === 'info' && (
                    <div>
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label form-label-sm fw-semibold">Name <span className="text-danger">*</span></label>
                          <input className="form-control form-control-sm" value={editForm.name}
                            onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label form-label-sm fw-semibold">Label Name</label>
                          <input className="form-control form-control-sm" value={editForm.labelName}
                            onChange={e => setEditForm(f => ({ ...f, labelName: e.target.value }))}
                            placeholder={editForm.name || 'e.g. Wheat Flour'} />
                          <div className="form-text" style={{ fontSize: 11 }}>
                            {!editForm.labelName
                              ? <>Used on the FDA ingredient statement — if empty, the ingredient <strong>Name</strong> is used.</>
                              : 'Used on the FDA ingredient statement.'}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label form-label-sm fw-semibold">Manufacturer <span className="text-danger">*</span></label>
                          <SupplierPickerField
                            options={supplierOptions} value={editForm.manufacturerId}
                            onChange={v => setEditForm(f => ({ ...f, manufacturerId: v }))}
                            onCreated={loadSupplierOptions} placeholder="Casillo"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label form-label-sm fw-semibold">Supplier</label>
                          <SupplierPickerField
                            options={supplierOptions} value={editForm.supplierId}
                            onChange={v => setEditForm(f => ({ ...f, supplierId: v }))}
                            onCreated={loadSupplierOptions} placeholder="If different from manufacturer"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label form-label-sm fw-semibold">Category</label>
                          <input className="form-control form-control-sm" value={editForm.category}
                            onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                            placeholder="e.g. Flour, Oil" />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label form-label-sm fw-semibold">RM Code</label>
                          <input className="form-control form-control-sm" value={editForm.rawMaterialCode}
                            onChange={e => setEditForm(f => ({ ...f, rawMaterialCode: e.target.value }))}
                            placeholder="e.g. FL-001" />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label form-label-sm fw-semibold">Case Size</label>
                          <div className="input-group input-group-sm">
                            <input type="number" min="0" step="0.001" className="form-control"
                              style={{ maxWidth: 80 }}
                              value={editForm.caseSize}
                              onChange={e => setEditForm(f => ({ ...f, caseSize: e.target.value }))}
                              placeholder="25" />
                            <select className="form-select form-select-sm" style={{ width: 74, flexShrink: 0, flexGrow: 0 }}
                              value={editForm.caseSizeUnit}
                              onChange={e => setEditForm(f => ({ ...f, caseSizeUnit: e.target.value }))}>
                              <option value="kg">kg</option>
                              <option value="lb">lb</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label form-label-sm fw-semibold">Case Label</label>
                          <input className="form-control form-control-sm" value={editForm.caseLabel}
                            onChange={e => setEditForm(f => ({ ...f, caseLabel: e.target.value }))}
                            placeholder="bag, case, drum…" />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label form-label-sm fw-semibold">QBO Expense Account</label>
                          <SearchableSelect
                            options={qboAccounts.map(a => ({ value: a.id, label: a.name }))}
                            value={editForm.qbExpenseAccountId}
                            onChange={v => setEditForm(f => ({ ...f, qbExpenseAccountId: v }))}
                            placeholder={qboAccounts.length ? 'Uses company default if unset' : 'Connect QuickBooks first'}
                            size="sm"
                          />
                          <div className="form-text" style={{ fontSize: 11 }}>For receipt-scan pushes.</div>
                        </div>
                        {isR365 && (
                          <div className="col-md-4">
                            <label className="form-label form-label-sm fw-semibold">R365 GL Account</label>
                            <input className="form-control form-control-sm" value={editForm.r365GLAccount}
                              onChange={e => setEditForm(f => ({ ...f, r365GLAccount: e.target.value }))}
                              placeholder="exact account name/code as it appears in R365" />
                            <div className="form-text" style={{ fontSize: 11 }}>
                              Uses company default if unset. For receipt-scan pushes to Restaurant 365.
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Sub-ingredients */}
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="form-label form-label-sm fw-semibold mb-0">Sub-ingredients</label>
                          <button type="button" className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }}
                            onClick={addSubIngredient}>+ Add</button>
                        </div>
                        <div className="form-text mb-1" style={{ fontSize: 11 }}>
                          Listed in parentheses on the ingredient statement, e.g. Butter (Cream, Salt).
                        </div>
                        {subIngredients.length === 0 ? (
                          <div className="text-muted" style={{ fontSize: 12 }}>None.</div>
                        ) : (
                          <div className="d-flex flex-column gap-1">
                            {subIngredients.map((sub, idx) => (
                              <div key={idx} className="d-flex gap-1">
                                <input type="text" className="form-control form-control-sm"
                                  placeholder="e.g. Pasteurized milk"
                                  value={sub.name}
                                  onChange={e => updateSubIngredient(idx, e.target.value)} />
                                <button className="btn btn-sm btn-outline-danger" onClick={() => removeSubIngredient(idx)}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Allergens */}
                      <div className="mb-4">
                        <label className="form-label form-label-sm fw-semibold mb-1">Allergens</label>
                        <div className="d-flex flex-wrap gap-3">
                          {ALLERGENS.map(a => (
                            <div key={a.key} className="form-check mb-0">
                              <input type="checkbox" className="form-check-input" id={`hub-allergen-${a.key}`}
                                checked={allergenFlags.includes(a.key)}
                                onChange={() => toggleAllergen(a.key)} />
                              <label className="form-check-label" htmlFor={`hub-allergen-${a.key}`} style={{ fontSize: 12 }}>
                                {a.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* USDA/FSIS cross-check flag */}
                      <div className="mb-4 form-check">
                        <input type="checkbox" className="form-check-input" id="hub-meat-poultry"
                          checked={isMeatOrPoultry}
                          onChange={e => setIsMeatOrPoultry(e.target.checked)} />
                        <label className="form-check-label" htmlFor="hub-meat-poultry" style={{ fontSize: 12 }}>
                          Contains meat or poultry
                        </label>
                        <div className="form-text" style={{ fontSize: 11 }}>
                          Used to flag products for review if a recipe contains this ingredient but
                          isn't classified as USDA-regulated — doesn't affect labels directly.
                        </div>
                      </div>

                      {/* Archive — keeps the ingredient intact for old recipes/traceability
                          (can't be deleted once referenced) but drops it from the active list
                          and new-recipe pickers. */}
                      <div className="mb-4 form-check">
                        <input type="checkbox" className="form-check-input" id="hub-ingredient-active"
                          checked={editForm.isActive}
                          onChange={e => setEditForm(f => ({ ...f, isActive: e.target.checked }))} />
                        <label className="form-check-label" htmlFor="hub-ingredient-active" style={{ fontSize: 12 }}>
                          Active
                        </label>
                        <div className="form-text" style={{ fontSize: 11 }}>
                          Uncheck to archive — e.g. a one-off test ingredient or a discontinued
                          supplier item. Stays linked to any existing recipes/traceability records;
                          just drops out of the active list and new-recipe pickers. Re-check anytime.
                        </div>
                      </div>

                      <button className="btn btn-primary btn-sm" onClick={handleInfoSave} disabled={infoSaving}>
                        {infoSaving ? 'Saving…' : 'Save Changes'}
                      </button>

                      {/* ── Price History ── */}
                      <hr className="my-4" />
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="fw-semibold mb-0">
                            Price History
                            {currentPriceKg != null && (
                              <span className="ms-2 badge text-bg-success" style={{ fontSize: 11, fontWeight: 500 }}>
                                Current: ${Number(currentPriceKg).toFixed(4)}/kg
                              </span>
                            )}
                          </h6>
                        </div>

                        {/* Chart */}
                        {!loadingPrices && priceHistory.length >= 2 && (
                          <div className="mb-4 p-2 rounded border bg-white">
                            <PriceChart history={priceHistory} />
                          </div>
                        )}

                        {/* Add price form */}
                        <div className="p-3 rounded border bg-light mb-3">
                          <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>Record New Price</div>
                          <div className="row g-2 align-items-end">
                            <div className="col-md-4">
                              <label className="form-label form-label-sm">Price</label>
                              <div className="input-group input-group-sm">
                                <span className="input-group-text">$</span>
                                <input type="number" min="0" step="0.0001" className="form-control"
                                  placeholder="0.0000"
                                  value={newPrice.pricePerKg}
                                  onChange={e => setNewPrice(p => ({ ...p, pricePerKg: e.target.value }))} />
                                <select className="form-select form-select-sm" style={{ width: 74, flexShrink: 0, flexGrow: 0 }}
                                  value={newPrice.unit}
                                  onChange={e => setNewPrice(p => ({ ...p, unit: e.target.value }))}>
                                  <option value="kg">kg</option>
                                  <option value="lb">lb</option>
                                </select>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <label className="form-label form-label-sm">Effective from</label>
                              <input type="date" className="form-control form-control-sm"
                                value={newPrice.effectiveDate}
                                onChange={e => setNewPrice(p => ({ ...p, effectiveDate: e.target.value }))} />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label form-label-sm">Note <span className="text-muted">(optional)</span></label>
                              <input type="text" className="form-control form-control-sm"
                                placeholder="e.g. new supplier"
                                value={newPrice.note}
                                onChange={e => setNewPrice(p => ({ ...p, note: e.target.value }))} />
                            </div>
                            <div className="col-12">
                              <button className="btn btn-sm btn-primary"
                                onClick={handleAddPrice}
                                disabled={priceSaving || !newPrice.pricePerKg || !newPrice.effectiveDate}>
                                {priceSaving ? 'Saving…' : 'Record Price'}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Price table */}
                        {loadingPrices ? (
                          <div className="text-muted" style={{ fontSize: 13 }}>Loading…</div>
                        ) : priceHistory.length === 0 ? (
                          <div className="text-muted" style={{ fontSize: 13 }}>No prices recorded yet.</div>
                        ) : (
                          <div style={{ overflowX: 'auto' }}>
                            <table className="table table-sm table-bordered mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ fontSize: 12 }}>Effective from</th>
                                  <th style={{ fontSize: 12 }}>Price / kg</th>
                                  <th style={{ fontSize: 12 }}>Price / lb</th>
                                  <th style={{ fontSize: 12 }}>Note</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[...priceHistory]
                                  .sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate))
                                  .map(p => {
                                    const isLatest = latestPrice && p.id === latestPrice.id;
                                    return (
                                      <tr key={p.id} style={isLatest ? { fontWeight: 600 } : {}}>
                                        <td style={{ fontSize: 12 }}>
                                          {p.effectiveDate}
                                          {isLatest && <span className="ms-1 badge text-bg-success" style={{ fontSize: 10 }}>current</span>}
                                        </td>
                                        <td style={{ fontSize: 12 }}>${Number(p.pricePerKg).toFixed(4)}</td>
                                        <td style={{ fontSize: 12 }}>${(Number(p.pricePerKg) * LB_TO_KG).toFixed(4)}</td>
                                        <td style={{ fontSize: 12 }} className="text-muted">{p.note || '—'}</td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── Nutrition tab ── */}
                  {detailTab === 'nutrition' && (() => {
                    // Prefer docs filed under the seeded "Technical Data Sheet" category, but categories are
    // fully company-customizable (renamed/replaced via Manage Tabs), so a company that
    // reorganized theirs (e.g. into a custom "Ingredients TDS" tab with no systemKey) would
    // otherwise never see this feature at all — fall back to offering every linked document.
    const techSheetDocs = ingDocs.filter(d => d.category?.systemKey === 'technical_sheet');
    const specSheets = techSheetDocs.length > 0 ? techSheetDocs : ingDocs;
                    return (
                      <div>
                        {/* AI extraction area */}
                        {specSheets.length === 0 ? (
                          <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded"
                            style={{ background: '#f8f9fa', border: '1px dashed #ced4da', fontSize: 13 }}>
                            <span style={{ fontSize: 16 }}>✨</span>
                            <span className="text-muted">
                              Upload a <strong>Technical / Spec Sheet</strong> in the Documents tab to extract nutrition facts with AI.
                            </span>
                          </div>
                        ) : (
                          <div className="mb-3">
                            {/* Doc picker — shown when multiple spec sheets exist */}
                            {aiDocPickerOpen && specSheets.length > 1 && (
                              <div className="p-2 mb-2 rounded" style={{ background: '#f0f7ff', border: '1px solid #bfdbfe' }}>
                                <div className="fw-semibold mb-2" style={{ fontSize: 12 }}>Select spec sheet to extract from:</div>
                                <div className="d-flex flex-column gap-1">
                                  {specSheets.map(d => (
                                    <button
                                      key={d.id}
                                      className="btn btn-sm btn-outline-primary text-start"
                                      style={{ fontSize: 12 }}
                                      onClick={() => handleAiExtract(d.id)}
                                    >
                                      {d.name}
                                      {d.issueDate && <span className="text-muted ms-2">{d.issueDate}</span>}
                                    </button>
                                  ))}
                                </div>
                                <button className="btn btn-link btn-sm p-0 mt-1 text-muted" style={{ fontSize: 11 }}
                                  onClick={() => setAiDocPickerOpen(false)}>Cancel</button>
                              </div>
                            )}
                            <div className="d-flex align-items-center gap-2">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleAiExtract(null)}
                                disabled={aiExtracting}
                              >
                                {aiExtracting ? (
                                  <><span className="spinner-border spinner-border-sm me-1" role="status" />Extracting…</>
                                ) : (
                                  '✨ Extract Nutrition from Spec Sheet'
                                )}
                              </button>
                              {specSheets.length > 1 && !aiDocPickerOpen && (
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                  {specSheets.length} spec sheets available — click to choose
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {/* AI review card — sub-ingredients + allergens */}
                        {(aiSubIngredients.length > 0 || aiAllergensDraft.length > 0) && (
                          <div className="mb-3 p-3 rounded" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 13 }}>
                            <div className="fw-semibold mb-3" style={{ fontSize: 13 }}>
                              ✨ AI also found — review before applying
                            </div>

                            {aiSubIngredients.length > 0 && (
                              <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="fw-semibold" style={{ fontSize: 12 }}>Sub-ingredients</span>
                                  {aiSubApplied ? (
                                    <span className="text-success" style={{ fontSize: 12 }}>✓ Applied</span>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-outline-success py-0 px-2"
                                      style={{ fontSize: 11 }}
                                      onClick={() => {
                                        setSubIngredients(aiSubIngredients.map(name => ({ name })));
                                        setAiSubApplied(true);
                                        showToast('Sub-ingredients applied — review and save in the Info tab.');
                                      }}
                                    >
                                      Apply to Info tab
                                    </button>
                                  )}
                                </div>
                                <div className="d-flex flex-wrap gap-1">
                                  {aiSubIngredients.map((name, i) => (
                                    <span
                                      key={i}
                                      className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded"
                                      style={{ background: '#dcfce7', color: '#166534', fontSize: 11 }}
                                    >
                                      {name}
                                      <button
                                        type="button"
                                        className="btn-close"
                                        style={{ fontSize: 8 }}
                                        aria-label="Remove"
                                        onClick={() => setAiSubIngredients(prev => prev.filter((_, j) => j !== i))}
                                      />
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {aiAllergensDraft.length > 0 && (
                              <div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="fw-semibold" style={{ fontSize: 12 }}>Allergens</span>
                                  {aiAllergensApplied ? (
                                    <span className="text-success" style={{ fontSize: 12 }}>✓ Applied</span>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-outline-success py-0 px-2"
                                      style={{ fontSize: 11 }}
                                      onClick={() => {
                                        setAllergenFlags(aiAllergensDraft);
                                        setAiAllergensApplied(true);
                                        showToast('Allergens applied — review and save in the Info tab.');
                                      }}
                                    >
                                      Apply to Info tab
                                    </button>
                                  )}
                                </div>
                                <div className="d-flex flex-wrap gap-3">
                                  {ALLERGENS.map(a => (
                                    <div key={a.key} className="form-check mb-0">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`ai-allergen-draft-${a.key}`}
                                        checked={aiAllergensDraft.includes(a.key)}
                                        onChange={() => setAiAllergensDraft(prev =>
                                          prev.includes(a.key)
                                            ? prev.filter(k => k !== a.key)
                                            : [...prev, a.key]
                                        )}
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor={`ai-allergen-draft-${a.key}`}
                                        style={{ fontSize: 12 }}
                                      >
                                        {a.label}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <IngredientNutritionRow
                          ingredient={{
                            ingredientId: selected.id,
                            name: selected.name,
                            expectedQuantityKg: null,
                          }}
                          onSaved={() => { setAiPrefill(null); onIngredientsChange(); }}
                          defaultExpanded={true}
                          prefillData={aiPrefill}
                          prefillConfidence={aiConfidence}
                          prefillPlausibility={aiPlausibility}
                          onDirty={() => setNutritionDirty(true)}
                          onClean={() => setNutritionDirty(false)}
                        />
                      </div>
                    );
                  })()}

                  {/* ── Documents tab ── */}
                  {detailTab === 'documents' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-semibold mb-0">Supplier Documents</h6>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setShowUploadForm(v => !v)}
                        >
                          {showUploadForm ? '✕ Cancel' : '+ Upload Document'}
                        </button>
                      </div>

                      {/* Upload form */}
                      {showUploadForm && (
                        <form onSubmit={handleUploadSubmit} className="p-3 border rounded bg-light mb-3">
                          <div className="row g-2 mb-2">
                            <div className="col-12">
                              <label className="form-label form-label-sm fw-semibold">File <span className="text-danger">*</span></label>
                              <input
                                ref={fileInputRef}
                                type="file" accept=".pdf,.jpg,.jpeg,.png"
                                className="form-control form-control-sm"
                                onChange={handleFileSelect}
                              />
                              <div className="form-text" style={{ fontSize: 11 }}>PDF, JPEG, or PNG</div>
                            </div>
                            <div className="col-md-7">
                              <label className="form-label form-label-sm fw-semibold">Document Name <span className="text-danger">*</span></label>
                              <input
                                className="form-control form-control-sm"
                                value={uploadForm.name}
                                onChange={e => setUploadForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. Flour Casillo COA 2024"
                              />
                            </div>
                            <div className="col-md-5">
                              <label className="form-label form-label-sm fw-semibold">Tab <span className="text-danger">*</span></label>
                              <select
                                className="form-select form-select-sm"
                                value={uploadForm.documentCategoryId}
                                onChange={e => setUploadForm(f => ({ ...f, documentCategoryId: e.target.value }))}
                              >
                                <option value="">— Select tab —</option>
                                {docCategories.filter(c => c.isActive).map(c => (
                                  <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-5">
                              <label className="form-label form-label-sm fw-semibold">Expiry Date</label>
                              <input
                                type="date" className="form-control form-control-sm"
                                value={uploadForm.expiryDate}
                                onChange={e => setUploadForm(f => ({ ...f, expiryDate: e.target.value }))}
                              />
                            </div>
                            <div className="col-md-7">
                              <label className="form-label form-label-sm fw-semibold">Notes</label>
                              <input
                                className="form-control form-control-sm"
                                value={uploadForm.notes}
                                onChange={e => setUploadForm(f => ({ ...f, notes: e.target.value }))}
                                placeholder="Optional notes"
                              />
                            </div>
                          </div>
                          {uploading && (
                            <div className="progress mb-2" style={{ height: 6 }}>
                              <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
                            </div>
                          )}
                          <button
                            type="submit" className="btn btn-primary btn-sm"
                            disabled={uploading}
                          >
                            {uploading ? `Uploading… ${uploadProgress}%` : 'Upload'}
                          </button>
                        </form>
                      )}

                      {/* Document list */}
                      {loadingDocs ? (
                        <div className="text-muted" style={{ fontSize: 13 }}>Loading…</div>
                      ) : ingDocs.length === 0 ? (
                        <div className="text-muted" style={{ fontSize: 13 }}>No documents uploaded yet.</div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {ingDocs.map(doc => (
                            <div key={doc.id} className="d-flex align-items-start gap-2 p-2 border rounded bg-white">
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="fw-semibold text-truncate" style={{ fontSize: 13 }}>{doc.name}</div>
                                <div className="d-flex flex-wrap gap-1 mt-1">
                                  {doc.category?.name && (
                                    <span className="badge text-bg-secondary" style={{ fontSize: 10, fontWeight: 400 }}>
                                      {doc.category.name}
                                    </span>
                                  )}
                                  <ExpiryBadge date={doc.expiryDate} />
                                </div>
                                {doc.notes && (
                                  <div className="text-muted mt-1" style={{ fontSize: 11 }}>{doc.notes}</div>
                                )}
                              </div>
                              <div className="d-flex gap-1 flex-shrink-0">
                                <a
                                  href={doc.fileUrl} target="_blank" rel="noreferrer"
                                  className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }}
                                >
                                  View
                                </a>
                                <button
                                  className="btn btn-outline-danger btn-sm" style={{ fontSize: 11 }}
                                  onClick={() => setConfirmDeleteDoc(doc)}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="mt-3">
                        <a href="/documents" target="_blank" rel="noreferrer"
                          className="text-muted" style={{ fontSize: 12 }}>
                          View all in Document Center →
                        </a>
                      </div>
                    </div>
                  )}

                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        show={!!pendingTab}
        title="Unsaved Nutrition Data"
        message="You have unsaved changes in the Nutrition tab. If you switch tabs now, your work will be lost. Save before switching."
        confirmLabel="Switch anyway"
        cancelLabel="Stay and save"
        variant="warning"
        onConfirm={() => { setNutritionDirty(false); setDetailTab(pendingTab); setPendingTab(null); }}
        onCancel={() => setPendingTab(null)}
      />

      <ConfirmModal
        show={!!confirmDeleteDoc}
        title="Delete Document"
        message={`Delete "${confirmDeleteDoc?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteDoc}
        onCancel={() => setConfirmDeleteDoc(null)}
      />

      <ConfirmModal
        show={confirmDelete}
        title="Delete Ingredient"
        message={`Are you sure you want to delete "${selected?.name}"? This cannot be undone. Products using this ingredient may be affected.`}
        confirmLabel={deleteSaving ? 'Deleting…' : 'Delete'}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      {/* ── Toast notification ── floats fixed, never shifts layout ── */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 2000,
            minWidth: 220,
            maxWidth: 340,
            pointerEvents: 'auto',
          }}
        >
          <div
            className={`d-flex align-items-center gap-2 px-3 py-2 rounded shadow`}
            style={{
              background: toast.type === 'danger' ? '#dc3545' : '#198754',
              color: '#fff',
              fontSize: 13,
              animation: 'fadeInUp 0.18s ease',
            }}
          >
            <span style={{ flex: 1 }}>{toast.msg}</span>
            <button
              type="button"
              onClick={() => setToast(null)}
              style={{ background: 'none', border: 'none', color: '#fff', opacity: 0.8, fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: '0 0 0 4px' }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <style>{`@keyframes fadeInUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}
